/**
 * SB6-ANA-CACHE-03 — Data Acquisitions list "Cached" cell.
 *
 * Per-row cache-completeness indicator derived from the image-cache manifest
 * (cached images / total images of the dataset). Shows a percentage with a
 * tooltip "312/369 images" (AC6); pinned datasets carry a pin glyph on the same
 * cell. Uncached datasets (no manifest entry, or 0 cached) read "—".
 *
 * Subscribes to the manifest store via `useCacheManifestEntry`, so it updates
 * live while a download runs in another tab/the analysis surface (AC6).
 */
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { useCacheManifestEntry } from "../analysis/cache/useCacheManifest.js";
import { PinIcon } from "@aeriosiq/ui/icons";

export function DatasetCacheCell({
  dataAcquisitionId,
}: {
  dataAcquisitionId: string;
}): React.ReactElement {
  const entry = useCacheManifestEntry(dataAcquisitionId);
  const cached = entry?.imageKeys.length ?? 0;
  const total = entry?.totalImageCount ?? 0;
  const pinned = entry?.pinned ?? false;

  if (!entry || cached === 0) {
    return (
      <Typography
        variant="body2"
        color="text.disabled"
        data-testid={`da-cache-cell-${dataAcquisitionId}`}
        aria-label="Not cached"
      >
        —
      </Typography>
    );
  }

  const pct = total > 0 ? Math.round((cached / total) * 100) : null;
  const label = pct != null ? `${String(pct)} %` : `${String(cached)} cached`;
  const tooltip =
    total > 0
      ? `${String(cached)}/${String(total)} images cached${
          pinned ? " · pinned" : ""
        }`
      : `${String(cached)} images cached${pinned ? " · pinned" : ""}`;

  return (
    <Tooltip title={tooltip}>
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        // ANA-R4-DA-01 — stretch to the full cell height so the % chip is
        // vertically centred in the row (was sitting high in compact density).
        sx={{ height: "100%" }}
        data-testid={`da-cache-cell-${dataAcquisitionId}`}
        data-cache-pct={pct ?? ""}
        data-cache-pinned={pinned ? "true" : "false"}
      >
        {pinned ? (
          <PinIcon
            sx={{ fontSize: 14, color: "primary.main" }}
            aria-label="Pinned"
          />
        ) : null}
        <Chip
          size="small"
          variant={pct === 100 ? "filled" : "outlined"}
          color={pct === 100 ? "success" : "default"}
          label={<Box component="span">{label}</Box>}
          sx={{ height: 22 }}
        />
      </Stack>
    </Tooltip>
  );
}
